/**
 * GPT-Live identity and per-deployment settings: its voices, its fixed wire
 * model, and the two fields that choose how a session reasons.
 */

import { z } from "zod";

/**
 * GPT-Live voices, each PROVEN ACCEPTED against the live API on 2026-09-15.
 *
 * An unknown voice is refused BEFORE the session exists, with
 * `{"code":"forbidden","message":"Voice session access denied."}` — the same
 * answer a bad key gets, so a typo here is indistinguishable from an auth
 * failure at the call site. That is why this list is probed rather than copied:
 * every name below was sent as `audio.output.voice` and echoed back on
 * `session.started`, and a coined name (`zzz-not-a-voice`) was refused in the
 * same run, so the probe could say no.
 *
 * It matches {@link OPENAI_REALTIME_VOICES} name for name today, and is written
 * out rather than aliased to it deliberately: a voice added to the Realtime
 * registry has NOT been probed against GPT-Live, and inheriting it would ship
 * an unprobed name whose failure reads as an auth failure.
 */
export const GPT_LIVE_VOICES = [
  "alloy",
  "ash",
  "ballad",
  "cedar",
  "coral",
  "echo",
  "marin",
  "sage",
  "shimmer",
  "verse"
] as const;
export type GptLiveVoice = (typeof GPT_LIVE_VOICES)[number];

/**
 * GPT-Live wire identity, for every surface that needs the model id or a
 * display name — the settings UI, tracing, and the relay adapter.
 *
 * `gpt-live-1` is not a Realtime model and is not selectable per deployment:
 * it is served only on `/v1/live/sessions`, so the model id and that endpoint
 * travel together (the endpoint itself stays in the adapter, which is the only
 * thing that opens a socket).
 *
 * The BACKEND model a session delegates tool requests to is a different thing
 * entirely, chosen per deployment — see {@link GptLiveDelegationModelSchema}.
 */
export const GPT_LIVE_MODEL_ID = "gpt-live-1";
export const GPT_LIVE_MODEL_DISPLAY_NAME = "GPT-Live-1";
/** Echoed back by `session.started` when the session names no voice. */
export const GPT_LIVE_DEFAULT_VOICE: GptLiveVoice = "marin";

/**
 * The backend (Responses API) model a GPT-Live session delegates to.
 *
 * REQUIRED whenever `provider === "gpt-live"`, and that requirement is a
 * refinement on the WRITE path only — see `requireGptLiveDelegationModel`. It
 * is not a required field here for two reasons:
 *
 *   - every other provider must still parse with the field absent, including
 *     every row written before GPT-Live existed; and
 *   - these settings are parsed on READ by the very editor that sets the field
 *     (`views/phone-call/tabs/VoiceTab` returns `null` when the parse fails),
 *     so a read-side refusal would hide the picker a deployment needs in order
 *     to become valid — including the moment between picking the provider and
 *     picking the model.
 *
 * Not validated against the model catalog: the catalog is DB-backed at runtime
 * and a deployment must keep parsing after a model is retired from it.
 */
export const GptLiveDelegationModelSchema = z.string().min(1).optional();

/**
 * Whether this deployment's GPT-Live sessions run SPEECH-TO-SPEECH rather than
 * through the Responses backend.
 *
 * The two modes are set by `session.delegation.type` in the `session.start`
 * frame, and that field is IMMUTABLE after start — `session.update` with a
 * different type is refused (`immutable_field_update`). So this is a per-session
 * decision made once, and both modes are live: turning it off returns the
 * deployment to the delegated path with nothing else to change.
 *
 * `undefined` reads as OFF. It is `.catch(undefined)` for the same reason
 * `provider` is: a legacy row carrying junk here must still parse, because these
 * settings are parsed on READ by the editor that sets the field.
 */
export const GptLiveNativeModeSchema = z.boolean().optional().catch(undefined);
